import RefreshIcon from "@mui/icons-material/CachedRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PortalIcon from "@mui/icons-material/LanguageRounded";
import ExportIcon from "@mui/icons-material/OutputRounded";
import { Button, Container, Divider, Stack } from "@mui/material";
import { useCallback, useReducer, useState } from "react";
import { useLocation } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { last12Months, thisQuarter, thisYear } from "@entrilia/shared/utilities/dateFilterHelper";
import adminApi from "../../../api/adminApi";
import type {
  DocumentCollectionOperations,
  DocumentCollectionPortalPublishStatus,
  DocumentCollectionPublishStatus,
} from "../../../api/types/documentCollectionTypes";
import useClientContext from "../../../context/useClientContext";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import ChangeDocumentCollectionsStatusDialog from "./ChangeDocumentCollectionsStatusDialog";
import DocumentCollectionsGrid from "./DocumentCollectionsGrid";
import { DocumentCollectionsPageContextProvider } from "./DocumentCollectionsPageContextProvider";
import {
  defaultDateRange,
  getInitialState,
  portalStatusFilterOptions,
  reducer,
  statusFilterOptions,
} from "./documentCollectionsState";

const getDownloadUrlForDocumentCollection = withErrorHandling(adminApi.getDownloadUrlForDocumentCollection);
const exportDocumentCollectionsToExcel = withErrorHandling(adminApi.exportDocumentCollectionsToExcel);

interface DialogState {
  openDialog?: "change_status";
  operation?: DocumentCollectionOperations;
  documentCollectionIds?: string[];
  documentCollectionsInfo?: string;
}

type LocationState = { documentCollectionId?: string } | null;

const DocumentCollectionsPage = () => {
  usePageTitle("Document Collections");

  const { clientCode, hasPermissions, investorPortalApp } = useClientContext();
  const location = useLocation();

  const [state, dispatch] = useReducer(reducer, getInitialState(clientCode));
  const [dialogState, setDialogState] = useState<DialogState>({});
  const [exporting, setExporting] = useState(false);

  const getDocumentCollections = useCallback(
    () =>
      adminApi.getDocumentCollections({
        dateFrom: state.dateRange.from?.toISOString(),
        dateTo: state.dateRange.to?.toISOString(),
      }),
    [state.dateRange.from, state.dateRange.to]
  );

  const [resp, fetchError, { isFetching, fetch }] = useFetch(getDocumentCollections, {
    onSetData: (data) => dispatch({ type: "LOAD_DATA", items: data.documentCollections }),
  });

  if (fetchError) {
    logError(fetchError, "[DocumentCollections]");
    return <DataLoadingFailed title="Loading document collections failed" />;
  }

  const handleSearch = (searchTerm: string) => dispatch({ type: "SEARCH", searchTerm });

  const handleDateRangeChange = (dateRange: DescriptiveDateRange) =>
    dispatch({ type: "SET_DATE_RANGE", dateRange, clientCode });

  const handleFundsFilterChange = (selectedFunds: string[]) => dispatch({ type: "FILTER_FUNDS", selectedFunds });

  const handleStatusFilterChange = (selectedStatuses: string[]) => {
    dispatch({ type: "FILTER_STATUSES", selectedStatuses: selectedStatuses as DocumentCollectionPublishStatus[] });
  };

  const handlePortalStatusFilterChange = (selectedPortalStatuses: string[]) => {
    dispatch({
      type: "FILTER_PORTAL_STATUSES",
      selectedPortalStatuses: selectedPortalStatuses as DocumentCollectionPortalPublishStatus[],
    });
  };

  const handleClearFilters = () => dispatch({ type: "CLEAR_FILTERS", clientCode });

  const handleRefresh = () => {
    fetch();
  };

  const handleExportToExcel = async () => {
    setExporting(true);
    const [resp, error] = await exportDocumentCollectionsToExcel();
    setExporting(false);
    if (error) {
      logError(error, "[DocumentCollectionsPage] exportDocumentCollectionsToExcel");
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);
  };

  const handleDownloadDocumentCollection = async (id: string) => {
    const [url, error] = await getDownloadUrlForDocumentCollection(id, clientCode);
    if (error) {
      logError(error, "[DocumentCollectionsPage] getDownloadUrlForDocumentCollection");
      return;
    }

    await downloadFileFromUrl(url);
  };

  const handleChangeDocumentCollectionsStatus = (
    documentCollectionIds: string[],
    operation: DocumentCollectionOperations
  ) => {
    if (documentCollectionIds.length === 0) {
      return;
    }

    let documentCollectionsInfo = `${documentCollectionIds.length} collections`;
    if (documentCollectionIds.length === 1) {
      const documentCollection = state.allItems.find((collection) => collection.id === documentCollectionIds[0]);
      if (documentCollection) {
        documentCollectionsInfo = documentCollection.name;
      }
    }

    setDialogState({ openDialog: "change_status", documentCollectionIds, operation, documentCollectionsInfo });
  };

  const showClearFilters =
    state.dateRange.from !== defaultDateRange.from ||
    state.dateRange.to !== defaultDateRange.to ||
    state.selectedFunds.length > 0 ||
    state.selectedStatuses.length > 0 ||
    state.selectedPortalStatuses.length > 0;

  const isLoading = resp === undefined || isFetching;

  return (
    <DocumentCollectionsPageContextProvider
      hasPermissionsToManage={hasPermissions(["ManageDocumentCollections"])}
      onDownloadDocumentCollection={handleDownloadDocumentCollection}
      onChangeDocumentCollectionsStatus={handleChangeDocumentCollectionsStatus}
    >
      <GeneralPageHeader title="Document Collections" />
      <Container maxWidth={false} sx={{ height: "100%", py: 2.5 }}>
        <Stack spacing={2} sx={{ width: "100%", height: "100%", pb: 5 }}>
          <Stack direction="row" sx={{ mt: 1, justifyContent: "space-between" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <DateRangeSelect
                predefinedRanges={[last12Months, thisQuarter, thisYear]}
                dateRange={state.dateRange}
                onChange={handleDateRangeChange}
                disabled={isLoading}
              />
              <Divider orientation="vertical" flexItem />
              <MultiSelectFilter
                placeholder="Funds"
                searchable
                options={state.allFunds}
                getOptionValue={(name) => name}
                getOptionLabel={(name) => name}
                selectedValues={state.selectedFunds}
                onChange={handleFundsFilterChange}
                StartIcon={CompaniesIcon}
                disabled={isLoading}
                width="16rem"
              />
              {investorPortalApp && (
                <MultiSelectFilter
                  placeholder="Investor Portal"
                  options={portalStatusFilterOptions}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  selectedValues={state.selectedPortalStatuses}
                  onChange={handlePortalStatusFilterChange}
                  StartIcon={PortalIcon}
                  disabled={isLoading}
                  width="12rem"
                />
              )}
              <MultiSelectFilter
                placeholder="Status"
                options={statusFilterOptions}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                selectedValues={state.selectedStatuses}
                onChange={handleStatusFilterChange}
                StartIcon={CheckCircleIcon}
                disabled={isLoading}
                width="12rem"
              />
              {showClearFilters && (
                <Button variant="text" color="primary" onClick={handleClearFilters}>
                  Clear all
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <SearchField
                initialValue={state.searchTerm}
                onSearch={handleSearch}
                disabled={isLoading}
                debounceTimeMs={300}
              />
              <Button
                variant="outlined"
                color="secondary"
                disabled={isLoading}
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
              <Button
                loading={exporting}
                color="secondary"
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExportToExcel}
                disabled={isLoading}
              >
                Export
              </Button>
            </Stack>
          </Stack>

          <DocumentCollectionsGrid
            documentCollections={state.filteredItems}
            loading={isLoading}
            highlightedRowId={(location.state as LocationState)?.documentCollectionId}
          />
        </Stack>
      </Container>
      {dialogState.openDialog === "change_status" &&
        dialogState.documentCollectionIds &&
        dialogState.operation &&
        dialogState.documentCollectionsInfo && (
          <ChangeDocumentCollectionsStatusDialog
            operationLevel="document_collections"
            selectedIds={dialogState.documentCollectionIds}
            operation={dialogState.operation}
            documentsInfo={dialogState.documentCollectionsInfo}
            onApply={handleRefresh}
            onClose={() => setDialogState({})}
          />
        )}
    </DocumentCollectionsPageContextProvider>
  );
};

export default DocumentCollectionsPage;
